/**
 * src/engine/estate.ts — terminal / estate (step-up) comparison (§6.15).
 *
 * At the horizon (treated as the death/liquidation event) under current US step-up
 * assumptions, compares the value left to heirs from borrowing vs. selling. Step-up wipes
 * the borrow path's unrealized gain — the BBD "tax alpha." Optional estate tax applies
 * above an exemption.
 */
import type { EstateResult, ScenarioInputs } from '@/types';
import type { BucketState } from './collateral';
import type { BorrowPathResult } from './projection';
import type { SellPathFull } from './borrowVsSell';
import { buildGainsTaxModel } from './gainsTax';

function portfolioValue(states: BucketState[]): number {
  return states.reduce((sum, b) => sum + b.value, 0);
}
function basisOf(states: BucketState[]): number {
  return states.reduce((sum, b) => sum + b.basis, 0);
}

/** Estate tax above the exemption, or 0 when disabled (§6.15). */
function estateTax(value: number, tax: ScenarioInputs['tax']): number {
  if (!tax.estateTaxEnabled) return 0;
  return (Math.max(0, value - tax.estateExemption) * tax.estateTaxRatePct) / 100;
}

export function computeEstate(
  inputs: ScenarioInputs,
  borrow: BorrowPathResult,
  sell: SellPathFull,
): EstateResult {
  // A fresh model: this is the hypothetical "sell it all in one year" tax the step-up
  // erases, so it stacks from a clean tax year rather than on the projection's history.
  const gainsTax = buildGainsTaxModel(inputs);

  const portfolioB = portfolioValue(borrow.finalStates);
  const basisB = basisOf(borrow.finalStates);
  const loan = borrow.summary.endingBalance;
  const estateValueBorrow = portfolioB - loan;

  const portfolioS = portfolioValue(sell.finalStates);

  const estateTaxBorrow = estateTax(estateValueBorrow, inputs.tax);
  const estateTaxSell = estateTax(portfolioS, inputs.tax);

  const borrowHeirsValue = estateValueBorrow - estateTaxBorrow;
  const sellHeirsValue = portfolioS - estateTaxSell;

  // Step-up erases the unrealized gain on the borrow path's still-held portfolio. With
  // brackets on, liquidating a large portfolio in one year walks the boundaries, so this
  // is integrated over the gain rather than multiplied by a single rate (§6.12b).
  const lifetimeIncomeTaxAvoided = gainsTax.taxOn(Math.max(0, portfolioB - basisB));

  return {
    borrowHeirsValue,
    sellHeirsValue,
    difference: borrowHeirsValue - sellHeirsValue,
    lifetimeIncomeTaxAvoided,
    estateTaxBorrow,
    estateTaxSell,
  };
}
